import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { addDays, formatDate, formatUSDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface EmployeeSummary {
  user_id: number | null
  name: string
  pay_rate: number | null
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  time_pay: number
  flat_rate_total: number
  mileage: number
  mileage_total: number
  expenses: number
  pto_hours: number
  sto_hours: number
}

interface PayPeriod {
  calc_type: number
  method_description: string
  week1_start: string
  week1_end: string
  week2_start: string
  week2_end: string
  employees: EmployeeSummary[]
  totals: EmployeeSummary | null
}

interface PendingTimeEntry {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  hours: number | null
}

interface PendingExpense {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  amount: number
}

interface LogNote {
  id: number
  date: string | null
  employee: string | null
  content: string
  hidden: boolean
}

interface Props {
  pay_period: PayPeriod
  pending: {
    unapproved_time_entries: PendingTimeEntry[]
    unapproved_expenses: PendingExpense[]
  }
  log_notes: LogNote[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(minutes: number): string {
  if (minutes === 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${String(m).padStart(2, "0")}m`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, "0")}m`
}

/* ── Columns ───────────────────────────────────────────── */

function buildEmployeeColumns(
  week1Start: string,
  week2End: string,
): HpiColumn<EmployeeSummary>[] {
  return [
    {
      header: "Employee",
      render: (row) => (
        <div className={`text-sm ${row.user_id ? "" : "font-bold"}`}>
          {row.user_id ? (
            <a
              href={`/team/activity?employee_id=${row.user_id}&start_date=${week1Start}&end_date=${week2End}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {row.name}
            </a>
          ) : (
            row.name
          )}
        </div>
      ),
    },
    {
      header: "Reg",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.regular_minutes)}
        </div>
      ),
    },
    {
      header: "OT",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatMinutes(row.overtime_minutes)}
        </div>
      ),
    },
  ]
}

function buildPendingTimeColumns(): HpiColumn<PendingTimeEntry>[] {
  return [
    {
      header: "Date",
      width: "max-w-[120px]",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}/time_entries/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.date)}
            </a>
          ) : (
            formatDate(row.date)
          )}
        </div>
      ),
    },
    {
      header: "Employee",
      render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
    },
    {
      header: "Case",
      render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
    },
    {
      header: "Hours",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">{row.hours?.toFixed(2) ?? "-"}</div>
      ),
    },
  ]
}

function buildPendingExpenseColumns(): HpiColumn<PendingExpense>[] {
  return [
    {
      header: "Date",
      width: "max-w-[120px]",
      render: (row) => (
        <div className="text-sm">
          {row.case_id ? (
            <a
              href={`/cases/${row.case_id}/expenses/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hpi-active-blue hover:underline"
            >
              {formatDate(row.date)}
            </a>
          ) : (
            formatDate(row.date)
          )}
        </div>
      ),
    },
    {
      header: "Employee",
      render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
    },
    {
      header: "Case",
      render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
    },
    {
      header: "Amount",
      width: "max-w-[100px]",
      render: (row) => (
        <div className="text-right text-sm">
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(row.amount)}
        </div>
      ),
    },
  ]
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Payroll Summary", href: "/accounting/payroll/summary" },
]

export default function PayrollSummary({
  pay_period,
  pending,
  log_notes,
}: Props) {
  const [endDate, setEndDate] = useState(pay_period.week2_end)

  const totals = pay_period.totals
  const filteredEmployees = pay_period.employees.filter(
    (e) => e.regular_minutes > 0 || e.overtime_minutes > 0,
  )
  const allData = totals ? [...filteredEmployees, totals] : filteredEmployees

  const employeeColumns = buildEmployeeColumns(
    pay_period.week1_start,
    pay_period.week2_end,
  )
  const pendingTimeColumns = buildPendingTimeColumns()
  const pendingExpenseColumns = buildPendingExpenseColumns()

  const handleChangeDateRange = () => {
    router.get("/accounting/payroll/summary", { end_date: endDate })
  }

  const prevEndDate = addDays(pay_period.week2_end, -14)
  const nextEndDate = addDays(pay_period.week2_end, 14)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payroll Summary" />

      <div className="p-6">
        {/* Simple header matching legacy */}
        <div className="mb-4">
          <h3 className="text-hpi-dark-gray text-lg font-semibold">
            Payroll Report
          </h3>
          <div className="text-hpi-dark-gray text-sm">
            {formatUSDate(pay_period.week1_start)} to{" "}
            {formatUSDate(pay_period.week2_end)}
          </div>
          {log_notes.length > 0 && (
            <a
              href="#logs"
              className="text-hpi-active-blue text-xs hover:underline"
            >
              {log_notes.length} Log Note{log_notes.length !== 1 ? "s" : ""}
            </a>
          )}
        </div>

        {/* Previous / Next Navigation (top) */}
        <div className="text-hpi-dark-gray mb-4 text-center text-sm">
          <span>&laquo; </span>
          <Link
            href={`/accounting/payroll/summary?end_date=${prevEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Previous Billing Period
          </Link>
          <span className="mx-1">|</span>
          <Link
            href={`/accounting/payroll/summary?end_date=${nextEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Next Billing Period
          </Link>
          <span> &raquo;</span>
        </div>

        {/* Date Range Picker */}
        <div className="border-hpi-light-gray mb-6 flex items-end gap-3 rounded-lg border bg-white px-4 py-3">
          <div>
            <label className="text-hpi-active-blue mb-1 block text-xs font-medium">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray focus:border-hpi-active-blue rounded border px-3 py-1.5 text-sm focus:outline-none"
            />
          </div>
          <button
            onClick={handleChangeDateRange}
            className="bg-hpi-dark-green rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Change Date Range
          </button>
        </div>

        {/* Unapproved entries above table */}
        {pending.unapproved_time_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingTimeEntry>
              title={`Unapproved Time Entries (${pending.unapproved_time_entries.length})`}
              columns={pendingTimeColumns}
              data={pending.unapproved_time_entries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {pending.unapproved_expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingExpense>
              title={`Unapproved Expenses (${pending.unapproved_expenses.length})`}
              columns={pendingExpenseColumns}
              data={pending.unapproved_expenses}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Employee Totals - 3 columns: Employee | Reg | OT */}
        <HpiDataTable<EmployeeSummary>
          title="Total for Pay Period"
          columns={employeeColumns}
          data={allData}
          keyFn={(row) => row.user_id ?? "totals"}
        />

        {/* Previous / Next Navigation */}
        <div className="text-hpi-dark-gray mt-6 text-center text-sm">
          <span>&laquo; </span>
          <Link
            href={`/accounting/payroll/summary?end_date=${prevEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Previous Billing Period
          </Link>
          <span className="mx-1">|</span>
          <Link
            href={`/accounting/payroll/summary?end_date=${nextEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Next Billing Period
          </Link>
          <span> &raquo;</span>
        </div>

        {/* Log Notes */}
        {log_notes.length > 0 && (
          <div id="logs" className="mt-8">
            <div className="border-hpi-light-gray mx-auto max-w-3xl rounded-lg border bg-white p-5">
              <h3 className="text-hpi-dark-gray mb-4 text-base font-semibold">
                Log Entries
                <Link
                  href="/reports/employee_logs"
                  className="text-hpi-active-blue ml-2 text-xs font-normal hover:underline"
                >
                  View All
                </Link>
              </h3>
              <div className="space-y-3">
                {log_notes.map((note) => (
                  <div
                    key={note.id}
                    className={note.hidden ? "text-hpi-med-gray" : ""}
                  >
                    <div className="text-sm font-bold">
                      {note.date ? formatUSDate(note.date) : ""} -{" "}
                      {note.employee ?? ""}
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
